import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useModel, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import EDEStatementsPageContainer from "components/EDEStatements/shared/containers/EDEStatementsPageContainer";
import { CenterContentContainer, ContentHeaderView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableView } from 'components/shared';
import Classes, { Account, CheckingAccount, CODAccount, LoanAccount, LOCAccount, SavingsAccount, User } from "model";
import "./styles.css";
import "./mobile.css";
import { PrimaryButton, SecondaryButton } from "components/ui";
import EDEStatementsEnrollmentPageContentView from "components/EDEStatements/shared/views/EDEStatementsEnrollmentPageContentView";

/**
 * @function EDEStatementsEnrollmentPreferencesConfirmationPage
 * @return {React.Component}
 * @exported true
 * @description The "EDE Statements Enrollment Preferences Confirmation" page.
 */
export default function EDEStatementsEnrollmentPreferencesConfirmationPage(props) {
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const model = useModel();
  const { branding } = useModel();

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(branding, model.user), ...(props.data || {}) };
  const ui = { ...getDefaultPageUI(), ...(props.ui || {}) };
  // The state to be passed to the state manager
  const state = { data, ui };
  const workflow = useWorkflow() || {};
  const { nextStep } = workflow;
  const accountTypeClassMap = {
    "checking": CheckingAccount,
    "savings": SavingsAccount,
    "loan": LoanAccount,
    "loc": LOCAccount,
    "cod": CODAccount
  };
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      onEnrollClick: evt => {
        if(typeof nextStep === "function") {
          nextStep();
        }
      },

      onDeliveryOptionChange: (prop, accountID, deliveryOption) => evt => {

        const updatedAccounts = state.data.accounts.map(account => {
          let updateAccount = undefined;

          // Explicitly set the updated prop since "init({ someprop: somval })" 
          // with the account types like "CheckingAccount" dont modify the values.
          if(account.id === accountID) {
            updateAccount = accountTypeClassMap[account.type].init({ ...account.record });
            updateAccount[prop] = deliveryOption.label;
          } else {
            updateAccount = accountTypeClassMap[account.type].init({ ...account.record });
            updateAccount[prop] = account[prop];
          }
          return updateAccount;
        });

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "accounts" ], updatedAccounts)
        .submit(setState);
      },
  });
  
  return(
    <EDEStatementsPageContainer className={ `ede-statements-enrollment-preferences-confirmation-page` } state={ state } controller={ controller }>
        <PageContent/>
    </EDEStatementsPageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (branding, user) => {
  const _user = user ? User.init(user) : {};

  // START: This code was copied from component "StatementPreferencePage".
  /* Static delivery options until we get a branding option for ede statements. */
  let deliveryOptions = [ "electronic", "US Mail" ].reduce((acc, option, index) => {
      acc.push({
          label: option,
          index: index,
      });
      return acc;
  }, []);

  /* Prefer business asset accounts when present; otherwise fall back to consumer asset accounts.
     Skip accounts that cannot be initialized (missing subtype rows in the model). */
  const businessAccounts = Account.initAllBusinessAssetOnly().filter(item => item && item.type !== "savings");
  const consumerAccounts = Account.initAll().filter(item => item && (item.type === "checking" || item.type === "savings" || item.type === "loc"));
  const sourceAccounts = businessAccounts.length > 0 ? businessAccounts : consumerAccounts;

  const accounts = sourceAccounts.reduce((acc, item, index) => {
      item.statementPreference = deliveryOptions[0].label;
      item.currIndex = index;
      if(index < 1) {
        acc.push(item);
      }
      return acc;
  }, []);

  return {
    accounts,
    defaultAccounts: accounts,
    deliveryOptions,
    contactInfo: _user.email,
  };
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = () => {
  return {

  };
};

/**
 * @function PageContent
 * @return {React.Component}
 * @exported false
 * @description The content for this page.
 */
function PageContent(props) {
  const { data, ui, onEnrollClick, onDeliveryOptionChange } = useStateManager();
  const { user } = useModel();
  const _user = User.init(user);
  const deliveryOptions =  [ ...data.deliveryOptions ];
  const brandableColumns = deliveryOptions.reduce((acc, item) => {
      acc.push(
          <TableColumnView key={ item.index } className="account-number-column" text={ item.label } />
      );
      return acc;
  }, []);

  const rows = data.accounts.reduce((acc, item) => {
      acc.push(
          <AccountTableRow key={ item.id } account={ item }/>
      );
      return acc;
  }, []);
  return (
      <EDEStatementsEnrollmentPageContentView>
        <CenterContentContainer>
            <ContentHeaderView text="Statement Notification - Email Address Confirmation" dataPoint={ `EDE_Enrollment_Preferences_Email_Address_Confirmation_Text` } />
            <p>
              <span>
                Confirm your email address below.
              </span>
            </p>
            <p>
              <span>
                Email Address:
              </span>
            </p>
            <div>
              <p>
                <span className="email-address-text" data-point={ `EDE_Enrollment_Preferences_Email_Address_Text` }>{ `${ data.contactInfo && data.contactInfo.value ? data.contactInfo.value : "" }` }</span>
              </p>
            </div>

            <ContentHeaderView text="Statement Delivery Preference Confirmation" dataPoint={ `EDE_Enrollment_Preferences_Preference_Confirmation_Text` } />
            <p>
              <span>{ `Click ENROLL to complete the enrollment process. NOTE: Once enrolled, your next statement will be available online.` } </span>
            </p>

            <TableView className="ede-others-statements-table">
                <TableColumnContainer>
                    <TableColumnView className="text-left date" text={ "Account Type" } />
                    <TableColumnView className="text-left description" text={ "Account Number" } />
                    { brandableColumns }
                </TableColumnContainer>
                <TableBody>
                    { rows }
                </TableBody>
            </TableView>

            <div className="flex-container flex-horizontal buttons-container">
              <div className="set-width button-container">
                <DisabledPrompt>
                  <SecondaryButton text="PREVIOUS" dataPoint={ `EDE_Enrollment_Preferences_Confirmation_Previous_Button` }></SecondaryButton>
                </DisabledPrompt>
              </div>
              <div className="set-width button-container">
                <PrimaryButton text="ENROLL" dataPoint={ `EDE_Enrollment_Preferences_Confirmation_Enroll_Button` } onClick={ onEnrollClick }></PrimaryButton>
              </div>
            </div>
        </CenterContentContainer>
      </EDEStatementsEnrollmentPageContentView>
  );
}

/**
 * @function AccountTableRow
 * @return {React.Component}
 * @exported false
 * @description A row in the table in the page.
 * @example
 * <AccountTableRow />
 */
function AccountTableRow(props) {
    const { data, ui, onUpdate, onCancel, onDeliveryOptionChange } = useStateManager();
    const account = props.account || Account.firstInstanceOf();
    let options =  [ ...data.deliveryOptions ];

    const brandedColumns = options.reduce((acc, item, index) => {
        const labelMap = {
            "electronic": "Electronic",
            "US Mail": "US_Mail"
        };

        const input = <input id={ `account_${ account.id }` } 
        onChange={ onDeliveryOptionChange("statementPreference", account.id, item) } 
        name={ `account_delivery_option_${ account.id }` } 
        type="radio" 
        checked={ account.statementPreference === item.label }
        disabled={ true }
        data-point={ `EDE_Enrollment_Preferences_Confirmation_Radio_${ labelMap[item.label] }_${ index }` } 
        />;

        acc.push(
            <TableItemTextView key={ item.index } className="account-number-column"
             text={ input } />
        );
        return acc;
    }, []);

    return (
      <TableItemContainer>
          <TableItemTextView className="account-nickname-column" text={ account.name }
          dataPoint={ `EDE_Enrollment_Preferences_Confirmation_Nickname_${ account.id }` }  />
          <TableItemTextView className="account-number-column" text={ account.number }
          dataPoint={ `EDE_Enrollment_Preferences_Confirmation_Number_${ account.id }` } />
          { brandedColumns }
      </TableItemContainer>
    );
}
